import { formatAutoVehicleLabel } from 'lib/autoTransport/formatAutoVehicleLabel';
import { asArrayOrObjectValues } from 'utils/asArrayOrObjectValues';
import { isValidNonDateObject } from 'utils/isValidObject';

/**
 * Normalizes suggested changes for all pipelines.
 *
 * Objects flatten nested properties:
 *  { carrier: { name: 'John' } } -> { carrier.name: 'John' }
 *
 * Arrays usually become their length:
 *  { trucks: [{id: 1}, {id: 2}] } -> { trucks: 2 }
 *
 * Exception: autoTransport.vehicles becomes a readable vehicle list string.
 */
export const flattenSuggestionChanges = (
  obj: Record<string, any>,
  parent?: string
): Record<string, any> => {
  let res: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const propName = parent ? parent + '.' + key : key;

    if (isValidNonDateObject(value)) {
      if (propName === 'autoTransport.vehicles') {
        res[propName] = formatAutoTransportVehicles(
          asArrayOrObjectValues(value as Record<string, unknown>)
        );
      } else if (Array.isArray(value)) {
        res[propName] = value.length;
      } else {
        res = {
          ...res,
          ...flattenSuggestionChanges(value as Record<string, any>, propName),
        };
      }
    } else {
      res[propName] = value;
    }
  }

  return res;
};

function formatAutoTransportVehicles(vehicles: unknown[]): string {
  return vehicles
    .map((vehicle) => {
      if (!vehicle || typeof vehicle !== 'object') {
        return '';
      }

      const record = vehicle as Record<string, unknown>;
      return formatAutoVehicleLabel({
        year:
          typeof record.year === 'string' || typeof record.year === 'number'
            ? record.year
            : undefined,
        make: typeof record.make === 'string' ? record.make : undefined,
        model: typeof record.model === 'string' ? record.model : undefined,
      });
    })
    .filter((label) => label.length > 0)
    .join(', ');
}
